/**
 * The component for col in FormBuilder
 *
 * This wraps col with configuration icons, it also display an empty placeholder if no children is present.
 *
 */
import React from "react";
import { injectIntl } from "react-intl";
import styled from "styled-components";
import EmptyColumnPlaceholder from "./EmptyColumnPlaceholder";
import { StyledCol, FakeCol } from "./styles";
import { useDrop } from "react-dnd";

const Bottom = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 10px 10px;
  align-items: center;
  opacity: 0;
  transition: opacity 125ms ease;
`;

const AddButton = styled.button`
  padding-bottom: 4px;
  background-color: #fff;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  font-size: 24px;
  color: #777;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  border-radius: 10px;

  &:focus {
    outline: none;
  }
`;

const SettingsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 20px;
  color: #777;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  transition: opacity 150ms ease;
  opacity: ${(p) => (p.hover ? 1 : 0)};

  &:hover {
    background-color: #efefef;
  }

  &:focus {
    outline: none;
  }
`;

const StyleIcon = styled.div`
  font-size: 24px;
  margin: 0px 20px;
  cursor: pointer;
`;

const StyleText = styled.div`
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  opacity: ${(p) => (p.selected ? "1" : "0.4")};
  margin: 0px 5px;
`;

const StyleTextHeading = styled.div`
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  width: 50px;
  text-align: right;
  margin-right: 10px;
  opacity: 0.5;
`;

const DropBox = styled.div`
  height: 40px;
  background: ${(p) => p.theme.colors.info};
  color: ${(p) => p.theme.colors.on.info};
  border: 1px solid ${(p) => p.theme.colors.border.info};
  border-radius: 8px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;

  & > i {
    margin-right: 8px;
  }
`;

const FakeDrop = styled.div`
  height: 50px;
  position: absolute;
  top: 0;
  width: 100%;
`;

const Wrapper = styled.div`
  background: repeating-linear-gradient(
    45deg,
    #fff,
    #fff 3px,
    #f1f1f1 3px,
    #f1f1f1 6px
  );
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover ${Bottom} {
    opacity: 1;
  }
`;

const Top = styled.div`
  flex: 1;
`;

export default injectIntl((props) => {
  const {
    extra = {},
    children = [],
    select,
    id,
    selected,
    theme = {},
    intl,
    save,
    fb3ClickMove,
    showAlignment,
  } = props;
  const { cols = 12, style = {} } = extra;
  const { col = {} } = theme;

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "component",
      drop: (item) => {
        const { action } = item;

        if (action === "create") {
          const { name, type, defaultExtra } = item;

          props.fb3Add({
            component: {
              name: type,
              extra: {
                label: name,
                name,
                ...defaultExtra,
              },
            },
            parent: id,
          });
        }

        if (item.nodeId !== id) {
          fb3ClickMove({ src: item.nodeId, dst: id, parentId: item.parentId });
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [id]
  );

  return (
    <StyledCol
      inlineStyle={{ ...col, ...style }}
      warning={children.length == 0}
      className={`col-md-${cols} `}
      hover={true}
    >
      {children.length == 0 ? (
        <EmptyColumnPlaceholder {...props} />
      ) : (
        <Wrapper>
          <Top>
            <FakeDrop ref={drop} />
            <FakeCol
              hoverConfig={selected}
              inlineStyle={style}
              className={`d-flex ${
                style.flexDirection
                  ? `flex-${style.flexDirection} ${
                      style.alignItems && `align-items-${style.alignItems}`
                    } ${
                      style.justifyContent &&
                      `justify-content-${style.justifyContent}`
                    }`
                  : "flex-column"
              }`}
            >
              {isOver && (
                <DropBox>
                  <i className="fa-regular fa-hand fa-beat"></i>{" "}
                  {intl.formatMessage({ id: "Drop to move" })}
                </DropBox>
              )}
              {children}
            </FakeCol>
          </Top>
          <Bottom>
            <div className="d-flex align-items-center">
              <SettingsButton
                hover={true}
                data-id={id}
                data-position="center"
                data-place="top"
                data-overlayposition="absolute"
                onClick={select}
                aria-label="Column settings"
              >
                <em className="fal fa-cog" />
              </SettingsButton>
              {showAlignment && (
                <>
                  <StyleIcon
                    className={`${
                      style.flexDirection === "column"
                        ? "fa fa-arrows-v"
                        : "fa fa-arrows-h"
                    }`}
                    onClick={() =>
                      save({
                        _id: id,
                        key: "style.flexDirection",
                        value:
                          style.flexDirection === "column" ? "row" : "column",
                      })
                    }
                    role="switch"
                    aria-label="Flex direction"
                  />
                  <div className="d-flex flex-column align-items-center">
                    <div className="d-flex align-items-center">
                      <StyleTextHeading>Align:</StyleTextHeading>
                      <div
                        className="d-flex align-items-center"
                        role="radiogroup"
                      >
                        <StyleText
                          selected={
                            style.alignItems === "start" || !style.alignItems
                          }
                          onClick={() =>
                            save({
                              _id: id,
                              key: "style.alignItems",
                              value: "start",
                            })
                          }
                          role="radio"
                          aria-checked={
                            style.alignItems === "start" || !style.alignItems
                          }
                        >
                          Start
                        </StyleText>
                        <StyleText
                          selected={style.alignItems === "center"}
                          onClick={() =>
                            save({
                              _id: id,
                              key: "style.alignItems",
                              value: "center",
                            })
                          }
                          role="radio"
                          aria-checked={style.alignItems === "center"}
                        >
                          Center
                        </StyleText>
                        <StyleText
                          selected={style.alignItems === "end"}
                          onClick={() =>
                            save({
                              _id: id,
                              key: "style.alignItems",
                              value: "end",
                            })
                          }
                          role="radio"
                          aria-checked={style.alignItems === "end"}
                        >
                          End
                        </StyleText>
                        <StyleText
                          selected={style.alignItems === "baseline"}
                          onClick={() =>
                            save({
                              _id: id,
                              key: "style.alignItems",
                              value: "baseline",
                            })
                          }
                          role="radio"
                          aria-checked={style.alignItems === "baseline"}
                        >
                          Baseline
                        </StyleText>
                        <StyleText
                          selected={style.alignItems === "strech"}
                          onClick={() =>
                            save({
                              _id: id,
                              key: "style.alignItems",
                              value: "strech",
                            })
                          }
                          role="radio"
                          aria-checked={style.alignItems === "strech"}
                        >
                          Stretch
                        </StyleText>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <StyleTextHeading>Justify:</StyleTextHeading>
                      <div
                        className="d-flex align-items-center"
                        role="radiogroup"
                      >
                        <StyleText
                          selected={
                            style.justifyContent === "start" ||
                            !style.justifyContent
                          }
                          onClick={() =>
                            save({
                              _id: id,
                              key: "style.justifyContent",
                              value: "start",
                            })
                          }
                          role="radio"
                          aria-checked={
                            style.justifyContent === "start" ||
                            !style.justifyContent
                          }
                        >
                          Start
                        </StyleText>
                        <StyleText
                          selected={style.justifyContent === "center"}
                          onClick={() =>
                            save({
                              _id: id,
                              key: "style.justifyContent",
                              value: "center",
                            })
                          }
                          role="radio"
                          aria-checked={style.justifyContent === "center"}
                        >
                          Center
                        </StyleText>
                        <StyleText
                          selected={style.justifyContent === "end"}
                          onClick={() =>
                            save({
                              _id: id,
                              key: "style.justifyContent",
                              value: "end",
                            })
                          }
                          role="radio"
                          aria-checked={style.justifyContent === "end"}
                        >
                          End
                        </StyleText>
                        <StyleText
                          selected={style.justifyContent === "between"}
                          onClick={() =>
                            save({
                              _id: id,
                              key: "style.justifyContent",
                              value: "between",
                            })
                          }
                          role="radio"
                          aria-checked={style.justifyContent === "between"}
                        >
                          Between
                        </StyleText>
                        <StyleText
                          selected={style.justifyContent === "around"}
                          onClick={() =>
                            save({
                              _id: id,
                              key: "style.justifyContent",
                              value: "around",
                            })
                          }
                          role="radio"
                          aria-checked={style.justifyContent === "around"}
                        >
                          Around
                        </StyleText>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <AddButton
              data-id={id}
              data-mode="add"
              data-position="center"
              data-overlayposition="absolute"
              onClick={select}
              aria-label="Add component"
            >
              +
            </AddButton>
          </Bottom>
        </Wrapper>
      )}
    </StyledCol>
  );
});
